import {useBrandItemStatusProblemsQuery} from "../../Queries";
import {Box, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";
import * as React from "react";

type ProblemsProps = {
   brandItemId: string
   shopId?: string
}

/**
 * Groups similar problems by extracting a common prefix and combining locale/currency codes.
 * E.g., "Name not defined for en", "Name not defined for da" => "Name not defined (en, da, ...)"
 */
function groupProblems(problems: string[]): { label: string; details: string }[] {
   const groups = new Map<string, string[]>();

   for (const problem of problems) {
      // Match patterns like "X for en" or "X for DKK"
      const match = problem.match(/^(.+?)\s+for\s+(\w+)(\s+\(.+\))?$/);
      if (match) {
         const [, prefix, code, suffix] = match;
         const key = prefix + (suffix || '');
         if (!groups.has(key)) {
            groups.set(key, []);
         }
         groups.get(key)!.push(code);
      } else {
         // Non-matching problems get their own entry
         groups.set(problem, []);
      }
   }

   const result: { label: string; details: string }[] = [];
   for (const [key, codes] of groups) {
      if (codes.length > 0) {
         const label = `${key} (${codes.join(', ')})`;
         result.push({ label, details: label });
      } else {
         result.push({ label: key, details: key });
      }
   }

   return result;
}

export const BrandItemMasterDataStatus = ({brandItemId, shopId}: ProblemsProps) => {
   const {data} = useBrandItemStatusProblemsQuery({
      variables: {
         brandItemId: brandItemId,
         shopId: shopId
      },
      fetchPolicy: "network-only"
   });
   if (!data) {
      return <CircularProgress size={"16px"}/>
   }

   const problems = data?.BrandItemProblems || [];

   if (problems.length === 0) {
      return <Typography variant="body2" color="success.main">OK</Typography>
   }

   const grouped = groupProblems(problems.filter(p => p) as string[]);
   const maxVisible = 3;
   const visible = grouped.slice(0, maxVisible);
   const remaining = grouped.length - maxVisible;

   return (
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, overflow: 'hidden'}}>
         {visible.map((item, index) => (
            <Chip
               key={index}
               label={item.label}
               color="error"
               variant="outlined"
               size="small"
               title={item.details}
               sx={{
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                     overflow: 'hidden',
                     textOverflow: 'ellipsis',
                     whiteSpace: 'nowrap'
                  }
               }}
            />
         ))}
         {remaining > 0 && (
            <Tooltip title={grouped.slice(maxVisible).map(g => g.label).join('\n')}>
               <Chip
                  label={`+${remaining} more`}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{}}
               />
            </Tooltip>
         )}
      </Box>
   )
}
